import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, Stethoscope, ShieldCheck, Clock } from "lucide-react";
import useDocumentMeta from "@/lib/useDocumentMeta";

const HIGHLIGHTS = [
    {
        title: "Пълна диагностика",
        desc: "Ще открием причината — от двигател и трансмисия до ABS, SRS и електроника.",
    },
    {
        title: "Фирмено оборудване",
        desc: "Работим с оригинални OEM и OBD-II скенери за над 30 марки.",
    },
    {
        title: "Ясен доклад",
        desc: "Получавате разбираемо обяснение и препоръчани стъпки за отстраняване.",
    },
];

export default function DiagnosticsPage() {
    useDocumentMeta({
        title: "Диагностика — Запазване на час | Konstantin Mobile",
        description:
            "Запишете час за пълна автомобилна диагностика с фирмено оборудване в Konstantin Mobile, Петрич. Онлайн резервация с автоматично изчисляване на свободни часове.",
        path: "/services/diagnostics",
    });

    return (
        <main className="relative pt-32 pb-24 min-h-screen" data-testid="diagnostics-page">
            <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-ink-muted hover:text-gold text-sm mb-8"
                >
                    <ArrowLeft size={16} /> Назад към сайта
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-10"
                >
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-4">
                        Услуги · Диагностика
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                        Записване за диагностика
                    </h1>
                    <p className="text-ink-muted max-w-2xl">
                        Изберете удобен ден и час — нашите техници ще извършат пълна
                        диагностика на Вашия автомобил с фирмено оборудване.
                    </p>
                </motion.div>

                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    {HIGHLIGHTS.map((h) => (
                        <div key={h.title} className="p-5 rounded-2xl bg-surface border border-line shadow-soft">
                            <Stethoscope size={18} className="text-gold mb-3" />
                            <div className="font-medium text-ink">{h.title}</div>
                            <p className="text-sm text-ink-muted mt-3 leading-relaxed">{h.desc}</p>
                        </div>
                    ))}
                </div>

                <div
                    className="bg-surface border border-line rounded-3xl p-8 lg:p-10 shadow-soft"
                    data-testid="diagnostics-appointment"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 text-gold flex items-center justify-center">
                            <CalendarClock size={20} />
                        </div>
                        <div>
                            <h2 className="font-display text-2xl">Онлайн резервация</h2>
                            <p className="text-sm text-ink-muted">
                                Автоматично изчисляване на свободни часове. Потвърждение по имейл.
                            </p>
                        </div>
                    </div>

                    <ul className="text-sm text-ink-muted space-y-2 mb-8">
                        <li className="flex items-center gap-2">
                            <Clock size={14} className="text-gold" />
                            Продължителност: около 60 мин
                        </li>
                        <li className="flex items-center gap-2">
                            <ShieldCheck size={14} className="text-gold" />
                            Работно време: Пон – Пет, 09:00 – 18:00
                        </li>
                    </ul>

                    <Link
                        to="/services/booking?category=diagnostics"
                        data-testid="diagnostics-book-cta"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-8 py-4 rounded-full text-sm font-medium shadow-gold transition-all"
                    >
                        Резервирай онлайн <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </main>
    );
}
